import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useHistory } from 'react-router-dom';
import LocalOpsSheet from './LocalOpsSheet';
import LocalOpsCompletionFlowHint from './LocalOpsCompletionFlowHint';
import PostMediaCarousel from '../post/PostMediaCarousel';
import { normalizeMediaList } from '../../utils/postMediaUtils';
import { getCoarseLocationLabel } from '../../utils/localOpsLocation';
import {
  getLocalOpsConfig,
  formatJGK,
  formatPercent,
} from '../../utils/localOpsConfig';
import { isCommunityTask } from '../../utils/localOpsCommunityTasks';
import axiosConn from '../../axiosConn';
import './localops.css';
import '../../styles/jgLocalOpsBadge.css';
import JGLocalOpsMark from '../common/JGLocalOpsMark';

const toLabel = (value) =>
  String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim() || 'Task';

const MONGO_ID_PATTERN = /^[a-f\d]{24}$/i;

const getObjectId = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return MONGO_ID_PATTERN.test(value) ? value : '';
  if (typeof value === 'object') {
    const raw = value._id || value.id || value.$oid || '';
    return typeof raw === 'string' && MONGO_ID_PATTERN.test(raw) ? raw : '';
  }
  return '';
};

const getSafeHandle = (value) => {
  if (!value) return '';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed && !MONGO_ID_PATTERN.test(trimmed) ? trimmed : '';
  }
  if (typeof value === 'object') {
    return firstText(value.playerId, value.handle, value.username, value.name);
  }
  return '';
};

const roundTo = (value, places = 2) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  const factor = 10 ** places;
  return Math.round(parsed * factor) / factor;
};

const approximatelyEqual = (left, right, tolerance = 0.01) =>
  Number.isFinite(left) &&
  Number.isFinite(right) &&
  Math.abs(left - right) <= tolerance;

const resolveTaskPriceUsd = (task) => {
  if (!task) return null;
  const candidates = [
    task.price,
    task.priceUsd,
    task.price_usd,
    task.meta?.price,
    task.meta?.priceUsd,
  ];
  for (const value of candidates) {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return null;
};

const normalizeTaskTrustRequirement = (value) =>
  String(value || '').trim().toLowerCase() === 'verified_local'
    ? 'verified_local'
    : 'open';

const GENERIC_LIVE_SUPPLY_COPY = {
  fanoutNotifiedCopy: 'Nearby eligible helpers are being notified.',
  verifiedLocalPriorityCopy: 'Verified Local helpers get priority.',
};
const DEFAULT_REOPEN_AFTER_MINUTES = 15;
const RIDE_HELP_TASKER_REVIEW_COPY =
  'Ride Help reminder: arrive in a clean, presentable vehicle with working A/C unless the requester agreed otherwise before the ride. The requester may cancel before the ride starts if the vehicle condition or A/C is not acceptable on arrival.';
const RIDE_HELP_ACCEPTED_HELPER_COPY =
  'Before you head out: clean, presentable vehicle and working A/C unless already agreed otherwise. The requester may cancel before the ride starts if it is not acceptable on arrival.';

const getCompletionProofSharePath = (task) => {
  const proof =
    task?.completionProofShare ||
    task?.proofShare ||
    task?.completion?.proofShare ||
    null;
  const path = proof?.sharePath || proof?.url || proof?.path || '';
  if (typeof path === 'string' && path.startsWith('/share/localops/proof/')) {
    return path;
  }
  const proofId = proof?.proofId || proof?.id || proof?._id || '';
  return proofId ? `/share/localops/proof/${encodeURIComponent(proofId)}` : '';
};

const stripApi = (url) =>
  typeof url === 'string' ? url.replace(/\/api\/v1\/?$/, '') : '';

const DEFAULT_PROOF_SHARE_BASE = 'https://api.joegalaxy.net';

const getProofShareOrigin = () =>
  stripApi(process.env.REACT_APP_SHARE_BASE_URL) ||
  stripApi(process.env.REACT_APP_AZURE_API_BASE_URL) ||
  stripApi(process.env.REACT_APP_API_BASE_URL_PROD) ||
  stripApi(process.env.REACT_APP_API_BASE_URL) ||
  DEFAULT_PROOF_SHARE_BASE;

const getCrawlerSafeProofShareUrl = (sharePath) => {
  if (!sharePath || typeof window === 'undefined') return sharePath || '';
  return new URL(sharePath, getProofShareOrigin()).toString();
};

const getReopenAfterMs = () => {
  const raw = Number(process.env.REACT_APP_LOCALOPS_ACCEPTED_TASK_REOPEN_AFTER_MINUTES);
  const minutes =
    Number.isFinite(raw) && raw > 0 ? raw : DEFAULT_REOPEN_AFTER_MINUTES;
  return minutes * 60 * 1000;
};

const getSupplyProof = (task) =>
  task?.localOpsSupplyProof ||
  task?.supplyProof ||
  task?.helperSupplyProof ||
  {};

const formatUsd = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return '$0.00';
  return `$${roundTo(parsed, 2).toFixed(2)}`;
};

const getMinimumRaiseUsd = (currentPriceUsd) => {
  const parsed = Number(currentPriceUsd);
  if (!Number.isFinite(parsed) || parsed <= 0) return 0;
  return roundTo(Math.min(5, Math.max(2, parsed * 0.15)), 2);
};

const makeRaisePayOperationKey = (task) => {
  const id = String(task?.taskId || task?.id || task?._id || 'task')
    .replace(/[^a-z0-9_-]/gi, '')
    .slice(0, 48);
  return `raise_pay_${id || 'task'}_${Date.now()}_${Math.floor(
    Math.random() * 1e6,
  )}`;
};

const redactLocationLabel = (label) => {
  if (!label) return '';
  const parts = String(label)
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
  if (!parts.length) return '';
  const first = parts[0];
  const looksLikeStreet =
    /\d/.test(first) ||
    /\b(st|street|ave|avenue|rd|road|ln|lane|dr|drive|blvd|boulevard|hwy|highway|pkwy|parkway|ct|court|trl|trail|pl|place)\b/i.test(
      first,
    );
  if (looksLikeStreet && parts.length > 1) {
    return parts.slice(1).join(', ') || 'Nearby';
  }
  if (looksLikeStreet) return 'Nearby';
  return parts.join(', ');
};

const firstText = (...values) => {
  for (const value of values) {
    if (typeof value !== 'string') continue;
    const trimmed = value.trim();
    if (trimmed) return trimmed;
  }
  return '';
};

const normalizeIdentityText = (value) => {
  if (value == null) return '';
  if (typeof value !== 'string' && typeof value !== 'number') return '';
  return String(value).trim();
};

const getIdentityId = (value) => {
  if (!value) return '';
  if (typeof value === 'object') {
    return normalizeIdentityText(value._id || value.id || value.$oid);
  }
  return normalizeIdentityText(value);
};

const normalizeHandleIdentity = (value) => {
  const text = normalizeIdentityText(value);
  return text && !MONGO_ID_PATTERN.test(text) ? text : '';
};

const getPublicIdentityHandle = (value) => {
  if (!value || typeof value !== 'object') return '';
  return normalizeHandleIdentity(firstText(value.playerId, value.handle));
};

const uniqueIdentityList = (values) =>
  Array.from(new Set(values.map(normalizeIdentityText).filter(Boolean)));

const uniqueHandleList = (values) =>
  Array.from(new Set(values.map(normalizeHandleIdentity).filter(Boolean)));

const matchesIdentityCandidate = (candidates, currentValue) => {
  const current = normalizeIdentityText(currentValue);
  return !!current && candidates.some((candidate) => candidate === current);
};

const getOwnerIdCandidates = (task) =>
  uniqueIdentityList([
    getIdentityId(task?.creatorPlayerId),
    getIdentityId(task?.creatorId),
    getIdentityId(task?.creator),
    getIdentityId(task?.ownerId),
    getIdentityId(task?.owner),
    getIdentityId(task?.requesterId),
    getIdentityId(task?.requester),
    getIdentityId(task?.requesterPlayer),
    getIdentityId(task?.createdBy),
    getIdentityId(task?.createdByPlayer),
  ]);

const getOwnerHandleCandidates = (task) =>
  uniqueHandleList([
    getPublicIdentityHandle(task?.creatorPlayerId),
    getPublicIdentityHandle(task?.creator),
    getPublicIdentityHandle(task?.owner),
    getPublicIdentityHandle(task?.requester),
    getPublicIdentityHandle(task?.requesterPlayer),
    getPublicIdentityHandle(task?.createdByPlayer),
    task?.ownerHandle,
    task?.creatorHandle,
    task?.requesterHandle,
  ]);

const getPrivatePickupAddress = (task) =>
  firstText(
    task?.originLocation?.address,
    task?.origin?.address,
    task?.pickupAddress,
    task?.meta?.pickupAddress,
  );

const getPrivateDeliveryAddress = (task) =>
  firstText(
    task?.meta?.deliveryAddress,
    task?.deliveryAddress,
    task?.destinationAddress,
    task?.meta?.destinationAddress,
    task?.destinationLocation?.address,
  );

const getCoordinateQuery = (coordinates) => {
  if (Array.isArray(coordinates) && coordinates.length >= 2) {
    const lon = Number(coordinates[0]);
    const lat = Number(coordinates[1]);
    if (Number.isFinite(lat) && Number.isFinite(lon)) return `${lat},${lon}`;
  }
  if (coordinates && typeof coordinates === 'object') {
    const lat = Number(coordinates.lat ?? coordinates.latitude);
    const lon = Number(coordinates.lon ?? coordinates.lng ?? coordinates.longitude);
    if (Number.isFinite(lat) && Number.isFinite(lon)) return `${lat},${lon}`;
  }
  return '';
};

const getMapsQuery = (address, coordinates) =>
  firstText(address, getCoordinateQuery(coordinates));

const getTaskDescriptionText = (task) =>
  firstText(task?.description, task?.summary, task?.meta?.description);

const buildGoogleMapsHref = ({ origin, destination }) => {
  const cleanOrigin = firstText(origin);
  const cleanDestination = firstText(destination);
  if (cleanOrigin && cleanDestination) {
    return `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(
      cleanOrigin,
    )}&destination=${encodeURIComponent(cleanDestination)}`;
  }
  const singleQuery = cleanDestination || cleanOrigin;
  if (!singleQuery) return '';
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    singleQuery,
  )}`;
};

const isRideLikeLocalOpsTask = (task) => {
  const text = [
    task?.title,
    task?.name,
    task?.meta?.title,
    task?.description,
    task?.summary,
    task?.meta?.description,
    task?.taskType,
    task?.category,
    task?.type,
    task?.meta?.taskType,
    task?.meta?.rideTemplate,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    task?.meta?.rideTemplate === 'collision_specialist_ride' ||
    /\bride\b|ride[-_\s]?like|assisted[-_\s]?transport|transportation|passenger/i.test(
      text,
    )
  );
};

const resolveDisplayTaskType = (task) => {
  const explicitType = firstText(
    task?.taskType,
    task?.category,
    task?.meta?.taskType,
  );
  if (explicitType) return explicitType;
  const transportType = firstText(task?.type);
  if (transportType && transportType !== 'task') return transportType;
  if (firstText(task?.meta?.deliveryAddress, task?.deliveryAddress)) {
    return 'delivery';
  }
  return transportType || 'task';
};

const buildPayoutSummary = (task, config, priceOverride) => {
  if (!task || !config) return null;
  const feePercent =
    Number.isFinite(task.platformFeePercent) && task.platformFeePercent > 0
      ? task.platformFeePercent
      : config.feePercent;
  const feeRate = feePercent / 100;
  const priceUsd =
    Number.isFinite(priceOverride) && priceOverride > 0
      ? priceOverride
      : resolveTaskPriceUsd(task);
  if (!Number.isFinite(priceUsd) || priceUsd <= 0) return null;
  const boostUsd = Number(task.platformBoostAmount);
  const boostAmountUsd =
    Number.isFinite(boostUsd) && boostUsd > 0 ? roundTo(boostUsd, 2) : 0;
  const baselineFeeUsd = Number(task.platformFeeAmountBeforeBoost);
  const settlementFeeUsd = Number(task.platformFeeAmount);
  const hasBaselineFee =
    boostAmountUsd > 0 && Number.isFinite(baselineFeeUsd) && baselineFeeUsd >= 0;
  const percentFeeUsd = roundTo(priceUsd * feeRate, 2);
  const hasSettlementFee =
    Number.isFinite(settlementFeeUsd) && settlementFeeUsd >= 0;
  const settlementFeeWithBoost = hasSettlementFee
    ? roundTo(settlementFeeUsd + boostAmountUsd, 2)
    : null;
  const settlementLooksBoostReduced =
    boostAmountUsd > 0 &&
    hasSettlementFee &&
    settlementFeeUsd < percentFeeUsd &&
    approximatelyEqual(settlementFeeWithBoost, percentFeeUsd);
  const taskFeeUsd = hasBaselineFee
    ? baselineFeeUsd
    : boostAmountUsd > 0 && settlementLooksBoostReduced
    ? settlementFeeWithBoost
    : hasSettlementFee
    ? settlementFeeUsd
    : percentFeeUsd;
  const feeUsd = roundTo(taskFeeUsd, 2);
  const taskNetUsd = Number(task.providerPayoutAmount);
  const computedNetUsd = roundTo(
    Math.max(0, priceUsd - feeUsd + boostAmountUsd),
    2,
  );
  const netUsd =
    boostAmountUsd > 0
      ? Number.isFinite(taskNetUsd) &&
        taskNetUsd > 0 &&
        approximatelyEqual(roundTo(taskNetUsd, 2), computedNetUsd)
        ? roundTo(taskNetUsd, 2)
        : computedNetUsd
      : Number.isFinite(taskNetUsd) && taskNetUsd > 0
      ? roundTo(taskNetUsd, 2)
      : computedNetUsd;
  const rate =
    Number.isFinite(config.jgkPerUsd) && config.jgkPerUsd > 0
      ? config.jgkPerUsd
      : 1000;
  return {
    totalJgkLabel: formatJGK(priceUsd * rate),
    totalUsdLabel: formatUsd(priceUsd),
    feePercentLabel: formatPercent(feePercent),
    feeJgkLabel: formatJGK(feeUsd * rate),
    feeUsdLabel: formatUsd(feeUsd),
    netJgkLabel: formatJGK(netUsd * rate),
    netUsdLabel: formatUsd(netUsd),
    boostJgkLabel: boostAmountUsd > 0 ? formatJGK(boostAmountUsd * rate) : null,
    boostUsdLabel: boostAmountUsd > 0 ? formatUsd(boostAmountUsd) : null,
    hasPlatformBoost: boostAmountUsd > 0,
  };
};

export default function LocalOpsTaskDetailSheet({
  open,
  task,
  onClose,
  onAccept,
  onStart,
  onComplete,
  onShare,
  onEdit,
  onMessage,
  onReopen,
  onRenotifyHelpers,
  onRaisePayAndRenotify,
  onRelease,
  currentPlayerId,
  currentPlayerHandle,
  currentPlayerRole = '',
  currentPlayerActionCreditCount = 0,
  verifiedLocalGateEnabled = false,
  hasOpenUnlockTask = false,
  onFindOpenTask = null,
  currentPlayerToken,
  onCancel,
  presenceEntry,
}) {
  const history = useHistory();
  const [startLocked, setStartLocked] = React.useState(false);
  const [renotifyLocked, setRenotifyLocked] = React.useState(false);
  const [raisePayOpen, setRaisePayOpen] = useState(false);
  const [raisePayAmount, setRaisePayAmount] = useState('');
  const [raisePayLocked, setRaisePayLocked] = useState(false);
  const [raisePayStatus, setRaisePayStatus] = useState('');
  const [raisePayMessage, setRaisePayMessage] = useState('');
  const [raisePayOperationKey, setRaisePayOperationKey] = useState('');
  const [capsule, setCapsule] = useState(null);
  const [capsuleError, setCapsuleError] = useState('');
  const [capsuleLoading, setCapsuleLoading] = useState(false);
  const [requesterProfile, setRequesterProfile] = useState(null);
  const mediaList = useMemo(
    () => (task ? normalizeMediaList(task) : []),
    [task],
  );
  useEffect(() => {
    setRenotifyLocked(false);
    setRaisePayOpen(false);
    setRaisePayAmount('');
    setRaisePayLocked(false);
    setRaisePayStatus('');
    setRaisePayMessage('');
    setRaisePayOperationKey('');
  }, [task?._id, task?.id, task?.taskId]);
  const localOpsConfig = useMemo(() => getLocalOpsConfig(), []);
  const toNumber = (value) => {
    if (value === '' || value === null || value === undefined) return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  };
  const rate =
    Number.isFinite(localOpsConfig?.jgkPerUsd) && localOpsConfig.jgkPerUsd > 0
      ? localOpsConfig.jgkPerUsd
      : 1000;
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.log('[LocalOpsTaskDetailSheet][task-snapshot]', {
        id: task?._id || task?.id || task?.taskId,
        price: task?.price,
        priceUsd: task?.priceUsd,
        price_usd: task?.price_usd,
        providerPayoutAmount: task?.providerPayoutAmount,
        requiredJGK: task?.requiredJGK ?? task?.requiredJgk ?? task?.required_jgk,
        escrowAmountJGK: task?.escrowAmountJGK ?? task?.escrowAmount ?? task?.escrow_amount,
        rate,
        rawTask: task,
        keys: task ? Object.keys(task) : [],
      });
    }
  }, [task, rate]);
  const priceUsd = useMemo(() => {
    const base = toNumber(resolveTaskPriceUsd(task));
    if (base !== null && base > 0) return base;
    const requiredJGK =
      toNumber(task?.requiredJGK) ??
      toNumber(task?.requiredJgk) ??
      toNumber(task?.required_jgk) ??
      toNumber(task?.escrowAmountJGK) ??
      toNumber(task?.escrowAmount) ??
      toNumber(task?.escrow_amount) ??
      null;
    if (Number.isFinite(requiredJGK) && requiredJGK > 0) {
      return requiredJGK / rate;
    }
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.log('[LocalOpsTaskDetailSheet][price-debug]', {
        priceCandidates: {
          price: task?.price,
          priceUsd: task?.priceUsd,
          price_usd: task?.price_usd,
          metaPrice: task?.meta?.price,
          metaPriceUsd: task?.meta?.priceUsd,
        },
        requiredJGK:
          task?.requiredJGK ??
          task?.requiredJgk ??
          task?.required_jgk ??
          task?.escrowAmountJGK ??
          task?.escrowAmount ??
          task?.escrow_amount ??
          null,
        rate,
        base,
      });
    }
    return base;
  }, [task, rate]);
  const payoutSummary = useMemo(
    () => buildPayoutSummary(task, localOpsConfig, priceUsd),
    [task, localOpsConfig, priceUsd],
  );
  const priceJgkRaw = useMemo(() => {
    const requiredJGK =
      toNumber(task?.requiredJGK) ??
      toNumber(task?.requiredJgk) ??
      toNumber(task?.required_jgk) ??
      toNumber(task?.escrowAmountJGK) ??
      toNumber(task?.escrowAmount) ??
      toNumber(task?.escrow_amount) ??
      null;
    if (Number.isFinite(requiredJGK) && requiredJGK > 0) return requiredJGK;
    if (Number.isFinite(priceUsd)) return priceUsd * rate;
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.log('[LocalOpsTaskDetailSheet][jgk-debug]', {
        requiredJGK:
          task?.requiredJGK ??
          task?.requiredJgk ??
          task?.required_jgk ??
          task?.escrowAmountJGK ??
          task?.escrowAmount ??
          task?.escrow_amount ??
          null,
        priceUsd,
        rate,
      });
    }
    return null;
  }, [task, priceUsd, rate]);

  const priceDisplayUsd = useMemo(() => {
    if (Number.isFinite(priceUsd)) return priceUsd;
    if (Number.isFinite(priceJgkRaw)) return priceJgkRaw / rate;
    return null;
  }, [priceUsd, priceJgkRaw, rate]);

  const priceJgkLabel = useMemo(() => {
    if (!Number.isFinite(priceJgkRaw)) return null;
    return formatJGK(priceJgkRaw);
  }, [priceJgkRaw]);
  const taskType = resolveDisplayTaskType(task);
  const label = toLabel(taskType);
  const communityTask = isCommunityTask(task);
  const taskDescriptionText = getTaskDescriptionText(task);
  const isDeliveryTask = taskType === 'delivery';
  const status = task?.status || 'discoverable';
  const ownerLabel =
    firstText(
      requesterProfile?.playerId,
      requesterProfile?.handle,
      requesterProfile?.username,
      task?.creator?.playerId,
      task?.creator?.handle,
      task?.creator?.username,
      task?.creatorPlayerId?.playerId,
      task?.creatorPlayerId?.handle,
      task?.creatorPlayerId?.username,
      task?.requester?.playerId,
      task?.requester?.handle,
      task?.requester?.username,
      task?.requesterPlayer?.playerId,
      task?.requesterPlayer?.handle,
      task?.createdByPlayer?.playerId,
      task?.createdByPlayer?.handle,
      task?.createdBy?.playerId,
      task?.createdBy?.handle,
      task?.owner?.playerId,
      task?.owner?.handle,
      task?.ownerHandle,
      task?.creatorHandle,
      task?.requesterHandle,
      task?.playerId,
      getSafeHandle(task?.creatorPlayerId),
      getSafeHandle(task?.ownerId),
      getSafeHandle(task?.requesterId),
      getSafeHandle(task?.createdBy),
    ) ||
    'Requester';
  const ownerPhoto =
    presenceEntry?.profilePhoto ||
    requesterProfile?.profilePhoto ||
    requesterProfile?.playerPhoto ||
    requesterProfile?.avatar ||
    task?.profilePhoto ||
    task?.creator?.profilePhoto ||
    task?.creator?.playerPhoto ||
    task?.creatorPlayerId?.profilePhoto ||
    task?.creatorPlayerId?.playerPhoto ||
    task?.requester?.profilePhoto ||
    task?.requester?.playerPhoto ||
    task?.requesterPlayer?.profilePhoto ||
    task?.requesterPlayer?.playerPhoto ||
    task?.createdByPlayer?.profilePhoto ||
    task?.createdByPlayer?.playerPhoto ||
    task?.createdBy?.profilePhoto ||
    task?.createdBy?.playerPhoto ||
    task?.owner?.profilePhoto ||
    task?.owner?.playerPhoto ||
    task?.ownerPhoto ||
    task?.avatar ||
    null;
  const requesterLookupId = useMemo(
    () =>
      getObjectId(task?.creatorPlayerId) ||
      getObjectId(task?.creator?._id || task?.creator?.id) ||
      getObjectId(task?.requester?._id || task?.requester?.id) ||
      getObjectId(task?.requesterPlayer?._id || task?.requesterPlayer?.id) ||
      getObjectId(task?.createdByPlayer?._id || task?.createdByPlayer?.id) ||
      getObjectId(task?.createdBy) ||
      getObjectId(task?.ownerId) ||
      getObjectId(task?.requesterId) ||
      '',
    [task],
  );
  useEffect(() => {
    setRequesterProfile(null);
    if (!open || !requesterLookupId || !currentPlayerToken) return undefined;
    let cancelled = false;
    const headers = { Authorization: `Bearer ${currentPlayerToken}` };
    axiosConn
      .get(`players/${requesterLookupId}`, { headers })
      .then((res) => {
        if (cancelled) return;
        const profile =
          res?.data?.data?.player ||
          res?.data?.player ||
          res?.data?.data ||
          res?.data ||
          null;
        if (profile && typeof profile === 'object') {
          setRequesterProfile(profile);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [currentPlayerToken, open, requesterLookupId]);
  const availability =
    task?.availabilityState ||
    task?.availability ||
    presenceEntry?.availabilityState ||
    presenceEntry?.status;
  const isPaymentReady =
    !communityTask &&
    Number.isFinite(priceDisplayUsd) &&
    priceDisplayUsd > 0 &&
    !task?.quoteRequested;
  const isQuoteRequired = task?.quoteRequested === true;
  const isFunded = task?.escrowStatus === 'held';
  const isCompleted = status === 'completed';
  const locationLabel = getCoarseLocationLabel(task) || 'Nearby';
  const postAcceptStatuses = new Set(['accepted', 'started', 'in_progress']);
  const startedStatuses = new Set(['started', 'in_progress']);
  const hasActiveAssignment = postAcceptStatuses.has(status);
  const ownerIdCandidates = getOwnerIdCandidates(task);
  const ownerHandleCandidates = getOwnerHandleCandidates(task);
  const isCreator =
    matchesIdentityCandidate(ownerIdCandidates, currentPlayerId) ||
    matchesIdentityCandidate(ownerHandleCandidates, currentPlayerHandle);
  const isAccepted =
    hasActiveAssignment &&
    ((currentPlayerId &&
      (task?.acceptedPlayerId === currentPlayerId ||
        task?.providerId === currentPlayerId ||
        task?.acceptedPlayer?.playerId === currentPlayerId ||
        task?.provider?.playerId === currentPlayerId)) ||
      (currentPlayerHandle &&
        (task?.acceptedPlayerId === currentPlayerHandle ||
          task?.providerId === currentPlayerHandle ||
          task?.acceptedPlayer?.playerId === currentPlayerHandle ||
          task?.provider?.playerId === currentPlayerHandle)));
  const isAdmin = String(currentPlayerRole || '') === 'admin';
  const viewerActionCreditTotal = Number(currentPlayerActionCreditCount || 0);
  const viewerIsVerifiedLocal =
    Number.isFinite(viewerActionCreditTotal) && viewerActionCreditTotal >= 1;
  const taskRequiresVerifiedLocal =
    normalizeTaskTrustRequirement(task?.trustRequirement) === 'verified_local';
  const verifiedLocalLocked =
    verifiedLocalGateEnabled &&
    taskRequiresVerifiedLocal &&
    !viewerIsVerifiedLocal &&
    !isCreator &&
    !isAccepted;
  const canShowAccept =
    ['created', 'discoverable', 'open', 'available'].includes(status) &&
    !hasActiveAssignment &&
    !isCreator;
  const canAccept =
    canShowAccept && !verifiedLocalLocked;
  const canStart =
    isAccepted &&
    !['completed', 'cancelled', 'expired', 'started', 'in_progress'].includes(
      status,
    ) &&
    (status === 'accepted' ||
      (status === 'discoverable' && isAccepted)); /* fallback guard */
  const showPayout = !communityTask && !!payoutSummary;
  const canCancel =
    isCreator && !['completed', 'cancelled', 'expired'].includes(status);
  const canEditPublicDetails =
    typeof onEdit === 'function' &&
    (isCreator || isAdmin) &&
    ['created', 'discoverable'].includes(status) &&
    !hasActiveAssignment;
  const shouldShare =
    isCreator &&
    onShare &&
    !['completed', 'cancelled', 'expired'].includes(status);
  const completionProofSharePath = getCompletionProofSharePath(task);
  const showCompletionProofShare =
    status === 'completed' && !!completionProofSharePath;
  const completionProofRegion =
    task?.regionLabel || task?.completionProofShare?.regionLabel || 'your area';
  const shouldShowAv =
    task?.avEligible || task?.avContext || task?.allowAv || task?.avEnabled;
  const pickupAddress = getPrivatePickupAddress(task);
  const deliveryAddress = getPrivateDeliveryAddress(task);
  const isCollisionSpecialistRide =
    task?.meta?.rideTemplate === 'collision_specialist_ride' ||
    /collision specialist/i.test(
      [
        task?.title,
        task?.name,
        task?.originLocation?.address,
        task?.meta?.pickupAddress,
      ]
        .filter(Boolean)
        .join(' '),
    );
  const isRideRequest = isRideLikeLocalOpsTask(task);
  const isRouteUnlockTask =
    isDeliveryTask ||
    isCollisionSpecialistRide ||
    isRideRequest ||
    !!deliveryAddress ||
    !!task?.destinationLocation?.address;
  const routeUnlockedForHelper =
    startedStatuses.has(status) || !!task?.startedAt;
  const canComplete =
    isCreator &&
    hasActiveAssignment &&
    (!isRouteUnlockTask || routeUnlockedForHelper);
  const acceptedHelperWaitingToStart =
    isAccepted &&
    status === 'accepted' &&
    isRouteUnlockTask &&
    !routeUnlockedForHelper;
  const showAcceptedPickupBeforeStart =
    acceptedHelperWaitingToStart && !!pickupAddress;
  const showPrivateAddresses =
    isCreator ||
    (isAccepted &&
      postAcceptStatuses.has(status) &&
      (!isRouteUnlockTask || routeUnlockedForHelper));
  const showPublicPickupAddress =
    !!pickupAddress && isCollisionSpecialistRide && !showPrivateAddresses;
  const showPickupAddress =
    !!pickupAddress &&
    (showPrivateAddresses ||
      showAcceptedPickupBeforeStart ||
      showPublicPickupAddress);
  const showDeliveryAddress = !!deliveryAddress && showPrivateAddresses;
  const mapsPickupQuery = showPickupAddress
    ? getMapsQuery(
        pickupAddress,
        task?.originLocation?.coordinates || task?.origin?.coordinates,
      )
    : '';
  const mapsDestinationQuery = showPrivateAddresses
    ? getMapsQuery(deliveryAddress, task?.destinationLocation?.coordinates)
    : '';
  const openMapsHref = buildGoogleMapsHref({
    origin: mapsPickupQuery,
    destination: mapsDestinationQuery,
  });
  const openMapsLabel =
    mapsPickupQuery && mapsDestinationQuery
      ? 'Open route in Maps'
      : mapsPickupQuery
      ? 'Open pickup in Maps'
      : mapsDestinationQuery
      ? 'Open destination in Maps'
      : 'Open address in Maps';
  const preStartPickupMapsHref =
    acceptedHelperWaitingToStart && mapsPickupQuery
      ? buildGoogleMapsHref({ origin: mapsPickupQuery })
      : '';
  const routeUnlockStartLabel = isRideRequest
    ? "I'm at pickup - reveal destination"
    : "I'm at pickup - reveal drop-off";
  const showUnlockedLocationPanel =
    isAccepted &&
    isRouteUnlockTask &&
    routeUnlockedForHelper &&
    showPrivateAddresses &&
    (!!pickupAddress || !!deliveryAddress || !!openMapsHref);
  const unlockedLocationPanelRef = React.useRef(null);
  useEffect(() => {
    if (!showUnlockedLocationPanel) return;
    const panel = unlockedLocationPanelRef.current;
    if (!panel) return;
    if (typeof panel.scrollIntoView === 'function') {
      panel.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [showUnlockedLocationPanel, task?._id, task?.id, task?.taskId, status]);
  const displayLocationLabel =
    showPrivateAddresses ? locationLabel : redactLocationLabel(locationLabel) || 'Nearby';
  const canShareLive =
    task?.liveTrackingEnabled &&
    (isCreator
      ? ['accepted', 'started', 'in_progress'].includes(status)
      : startedStatuses.has(status));
  const showShareHelper = canShareLive || shouldShare;
  const handleShareProof = useCallback(() => {
    if (!completionProofSharePath) return;
    const absoluteUrl = getCrawlerSafeProofShareUrl(completionProofSharePath);
    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      navigator
        .share({
          title: 'LocalOps completion proof',
          text: 'Post the task. Watch it get done.',
          url: absoluteUrl,
        })
        .catch(() => {});
      return;
    }
    history.push(completionProofSharePath);
  }, [completionProofSharePath, history]);
  const acceptedAtMs = task?.acceptedAt
    ? new Date(task.acceptedAt).getTime()
    : NaN;
  const reopenThresholdMet =
    isAdmin ||
    (Number.isFinite(acceptedAtMs) &&
      Date.now() - acceptedAtMs >= getReopenAfterMs());
  const hasEscrowStatusField = Object.prototype.hasOwnProperty.call(
    task || {},
    'escrowStatus',
  );
  const escrowHeldForReopen =
    task?.escrowStatus === 'held' ||
    (!hasEscrowStatusField &&
      Number.isFinite(priceJgkRaw) &&
      priceJgkRaw > 0);
  const payoutAllowsReopen =
    task?.payoutStatus == null ||
    String(task.payoutStatus || 'unpaid') === 'unpaid';
  const canReopen =
    typeof onReopen === 'function' &&
    (isCreator || isAdmin) &&
    status === 'accepted' &&
    !!task?.acceptedPlayerId &&
    !task?.startedAt &&
    reopenThresholdMet &&
    Number.isFinite(priceDisplayUsd) &&
    priceDisplayUsd > 0 &&
    task?.quoteRequested !== true &&
    escrowHeldForReopen &&
    payoutAllowsReopen;
  const canRelease =
    typeof onRelease === 'function' &&
    !isCreator &&
    isAccepted &&
    status === 'accepted' &&
    !!task?.acceptedPlayerId &&
    !task?.startedAt &&
    Number.isFinite(priceDisplayUsd) &&
    priceDisplayUsd > 0 &&
    task?.quoteRequested !== true &&
    escrowHeldForReopen &&
    payoutAllowsReopen;
  const acceptedPlayerIdRaw = hasActiveAssignment
    ? task?.accepted?.playerId ||
      task?.acceptedPlayer?.playerId ||
      task?.provider?.playerId ||
      task?.acceptedPlayerHandle ||
      task?.providerHandle ||
      task?.acceptedPlayerId ||
      task?.providerId ||
      null
    : null;
  const acceptedPlayerId =
    acceptedPlayerIdRaw &&
    !/^[a-f0-9]{24}$/i.test(String(acceptedPlayerIdRaw || '').trim())
      ? acceptedPlayerIdRaw
      : null;
  const acceptedPlayerPhoto =
    task?.accepted?.profilePhoto ||
    task?.acceptedPlayer?.profilePhoto ||
    task?.provider?.profilePhoto ||
    task?.providerPhoto ||
    null;
  const messageHelperTargetId = hasActiveAssignment
    ? task?.accepted?._id ||
      task?.acceptedPlayer?._id ||
      task?.provider?._id ||
      task?.acceptedPlayerId ||
      task?.providerId ||
      null
    : null;
  const canMessageHelper =
    typeof onMessage === 'function' &&
    (isCreator || isAdmin) &&
    hasActiveAssignment &&
    !!messageHelperTargetId;
  const statusLabel = (() => {
    if (['completed', 'cancelled', 'expired'].includes(status)) return status;
    if (status === 'in_progress' || status === 'started') return 'in_progress';
    if (status === 'accepted') return 'accepted';
    if (hasActiveAssignment && (acceptedPlayerId || isAccepted)) {
      return 'accepted';
    }
    return status || 'discoverable';
  })();
  const supplyProof = getSupplyProof(task);
  const ownerReNotifyProof =
    supplyProof.ownerReNotify || task?.localOpsOwnerReNotify || {};
  const ownerReNotifyNextEligibleMs = ownerReNotifyProof.nextEligibleOwnerReNotifyAt
    ? new Date(ownerReNotifyProof.nextEligibleOwnerReNotifyAt).getTime()
    : NaN;
  const ownerReNotifyRateLimited =
    Number.isFinite(ownerReNotifyNextEligibleMs) &&
    ownerReNotifyNextEligibleMs > Date.now();
  const canShowRenotifyHelpers =
    typeof onRenotifyHelpers === 'function' &&
    !communityTask &&
    (isCreator || isAdmin) &&
    ['created', 'discoverable'].includes(status) &&
    !hasActiveAssignment &&
    !task?.acceptedPlayerId &&
    isFunded &&
    isPaymentReady &&
    !['completed', 'cancelled', 'expired'].includes(status);
  const renotifyHelpersDisabled = renotifyLocked || ownerReNotifyRateLimited;
  const renotifyHelpersCopy = ownerReNotifyRateLimited
    ? 'Helpers were notified recently. Try again later.'
    : 'Still need help? Notify local helpers again.';
  const canShowRaisePay =
    typeof onRaisePayAndRenotify === 'function' &&
    !communityTask &&
    (isCreator || isAdmin) &&
    ['created', 'discoverable'].includes(status) &&
    !hasActiveAssignment &&
    !task?.acceptedPlayerId &&
    isFunded &&
    isPaymentReady &&
    !['completed', 'cancelled', 'canceled', 'expired'].includes(status);
  const minimumRaiseUsd = getMinimumRaiseUsd(priceDisplayUsd);
  const minimumRaisePriceUsd =
    Number.isFinite(priceDisplayUsd) && minimumRaiseUsd > 0
      ? roundTo(priceDisplayUsd + minimumRaiseUsd, 2)
      : null;
  const parsedRaisePayUsd = toNumber(raisePayAmount);
  const raisePayDeltaUsd =
    Number.isFinite(parsedRaisePayUsd) && Number.isFinite(priceDisplayUsd)
      ? roundTo(parsedRaisePayUsd - priceDisplayUsd, 2)
      : null;
  const raisePayValidationMessage = (() => {
    if (!raisePayOpen) return '';
    if (!Number.isFinite(parsedRaisePayUsd) || parsedRaisePayUsd <= 0) {
      return 'Enter a new task pay.';
    }
    if (
      Number.isFinite(priceDisplayUsd) &&
      parsedRaisePayUsd <= priceDisplayUsd
    ) {
      return `Enter more than the current ${formatUsd(priceDisplayUsd)} pay.`;
    }
    if (
      Number.isFinite(raisePayDeltaUsd) &&
      minimumRaiseUsd > 0 &&
      raisePayDeltaUsd < minimumRaiseUsd
    ) {
      return `Raise by at least ${formatUsd(minimumRaiseUsd)}.`;
    }
    return '';
  })();
  const raisePayReady =
    canShowRaisePay &&
    Number.isFinite(parsedRaisePayUsd) &&
    parsedRaisePayUsd > 0 &&
    !raisePayValidationMessage;
  const raisePayPreview = useMemo(() => {
    if (
      !task ||
      !Number.isFinite(parsedRaisePayUsd) ||
      parsedRaisePayUsd <= 0
    ) {
      return null;
    }
    return buildPayoutSummary(
      {
        ...task,
        price: parsedRaisePayUsd,
        priceUsd: parsedRaisePayUsd,
        platformFeeAmount: undefined,
        platformFeeAmountBeforeBoost: undefined,
        providerPayoutAmount: undefined,
        providerPayoutAmountBeforeBoost: undefined,
        platformBoostAmount: 0,
        platformBoostJGK: 0,
      },
      localOpsConfig,
      parsedRaisePayUsd,
    );
  }, [task, localOpsConfig, parsedRaisePayUsd]);
  const lifecycleProof = useMemo(() => {
    if (!isCreator) return null;
    if (status === 'completed') {
      return {
        title: isRideRequest ? 'Ride help completed.' : 'Task completed.',
        detail: '',
      };
    }
    if (status === 'started' || status === 'in_progress') {
      return {
        title: 'Helper is on the way.',
        detail: task?.etaMinutes
          ? `ETA: ${task.etaMinutes} min`
          : 'Live ETA coming soon.',
      };
    }
    if (status === 'accepted' || (hasActiveAssignment && (acceptedPlayerId || isAccepted))) {
      return {
        title: isRideRequest
          ? 'Helper accepted your ride request.'
          : 'Helper accepted your request.',
        detail: isRouteUnlockTask
          ? 'Waiting for helper to confirm they are on the way.'
          : 'Your helper can now see task details.',
      };
    }
    if (status === 'discoverable' || isFunded) {
      return {
        title: 'Request live.',
        detail:
          supplyProof.fanoutNotifiedCopy ||
          GENERIC_LIVE_SUPPLY_COPY.fanoutNotifiedCopy,
        priority:
          supplyProof.verifiedLocalPriorityCopy ||
          GENERIC_LIVE_SUPPLY_COPY.verifiedLocalPriorityCopy,
      };
    }
    return null;
  }, [
    acceptedPlayerId,
    hasActiveAssignment,
    isAccepted,
    isCreator,
    isFunded,
    isRideRequest,
    isRouteUnlockTask,
    status,
    supplyProof.fanoutNotifiedCopy,
    supplyProof.verifiedLocalPriorityCopy,
    task?.etaMinutes,
  ]);
  const handleCashoutNow = useCallback(() => {
    history.push('/jgktoken#cashout');
  }, [history]);
  const handleViewWallet = useCallback(() => {
    history.push('/jgktoken');
  }, [history]);

  useEffect(() => {
    const taskId = task?._id || task?.taskId || task?.id;
    const acceptedId = task?.acceptedPlayerId || task?.acceptedPlayer?._id;
    if (!open || !taskId || !acceptedId || !isCreator || !currentPlayerToken) {
      setCapsule(null);
      setCapsuleError('');
      return;
    }
    let cancelled = false;
    const fetchCapsule = async () => {
      setCapsuleLoading(true);
      setCapsuleError('');
      try {
        const headers = { Authorization: `Bearer ${currentPlayerToken}` };
        const res = await axiosConn.get(
          `local-ops/tasks/${taskId}/assignee-capsule`,
          { headers },
        );
        if (cancelled) return;
        setCapsule(res?.data?.data || null);
      } catch (err) {
        if (cancelled) return;
        setCapsuleError('Identity signals unavailable.');
      } finally {
        if (!cancelled) setCapsuleLoading(false);
      }
    };
    fetchCapsule();
    return () => {
      cancelled = true;
    };
  }, [open, task, isCreator, currentPlayerToken]);

  useEffect(() => {
    setStartLocked(false);
  }, [task?._id, status]);

  if (!task) return null;

  const defaultRaisePayAmount =
    Number.isFinite(minimumRaisePriceUsd) && minimumRaisePriceUsd > 0
      ? minimumRaisePriceUsd.toFixed(2)
      : '';

  const toggleRaisePayPanel = () => {
    const nextOpen = !raisePayOpen;
    setRaisePayOpen(nextOpen);
    setRaisePayStatus('');
    setRaisePayMessage('');
    if (nextOpen && !raisePayAmount && defaultRaisePayAmount) {
      setRaisePayAmount(defaultRaisePayAmount);
    }
  };

  const handleRaisePaySubmit = async (event) => {
    event.preventDefault();
    if (!canShowRaisePay || raisePayLocked) return;
    if (!raisePayReady) {
      setRaisePayStatus('error');
      setRaisePayMessage(
        raisePayValidationMessage || 'Enter a valid higher task pay.',
      );
      return;
    }
    const operationKey =
      raisePayOperationKey || makeRaisePayOperationKey(task);
    setRaisePayOperationKey(operationKey);
    setRaisePayLocked(true);
    setRaisePayStatus('pending');
    setRaisePayMessage('');
    try {
      const result = await onRaisePayAndRenotify(task, {
        newPriceUsd: parsedRaisePayUsd,
        idempotencyKey: operationKey,
      });
      const message =
        result?.message ||
        (result?.requiresFunding
          ? 'Top up to finish raising task pay.'
          : 'Task pay raise handled.');
      setRaisePayMessage(message);
      if (result?.requiresFunding) {
        setRaisePayStatus('pending_funding');
        return;
      }
      if (result?.ok === false) {
        setRaisePayStatus('error');
        if (result?.terminal) {
          setRaisePayOperationKey('');
        }
        return;
      }
      setRaisePayStatus('success');
      setRaisePayOperationKey('');
    } catch (err) {
      setRaisePayStatus('error');
      setRaisePayMessage(
        err?.message || 'Unable to raise task pay right now.',
      );
    } finally {
      setRaisePayLocked(false);
    }
  };

  const handleAvRequest = () => {
    if (typeof window === 'undefined') return;
    const id = task?.taskId || task?.id || task?._id;
    const providerId =
      task?.providerId || task?.assigneeId || task?.driverId || null;
    window.dispatchEvent(
      new CustomEvent('localops:av:request', {
        detail: { taskId: id, providerId },
      }),
    );
  };

  const actionCreditOutcomeGuaranteed =
    task?.actionCreditEligible !== false &&
    task?.actionCreditEligibleForViewer !== false &&
    task?.actionCredit?.eligible !== false &&
    task?.actionCredit?.duplicate !== true &&
    task?.verifiedRecord?.duplicate !== true;
  const viewerCanEarnTask =
    !isCreator && !verifiedLocalLocked && (canAccept || isAccepted);
  const taskPayoutLine = communityTask
    ? 'Community Task - no cash payout'
    : Number.isFinite(priceDisplayUsd) && priceDisplayUsd > 0
    ? viewerCanEarnTask
      ? `${formatUsd(priceDisplayUsd)} task total`
      : isCreator
      ? `${formatUsd(priceDisplayUsd)} task total`
      : `Payout: ${formatUsd(priceDisplayUsd)} after verification`
    : null;
  const netPayoutLine = payoutSummary
    ? isCreator
      ? `Helper receives ${payoutSummary.netUsdLabel} after platform fee${
          payoutSummary.hasPlatformBoost ? ' and local activity boost' : ''
        }.`
      : `You receive ${payoutSummary.netUsdLabel} after platform fee${
          payoutSummary.hasPlatformBoost ? ' and local activity boost' : ''
        }.`
    : '';
  const formatMoneyPair = (
    usdLabel,
    jgkLabel,
    { positive = false, negative = false } = {},
  ) => {
    const prefix = positive ? '+' : negative ? '-' : '';
    return `${prefix}${usdLabel} (${prefix}${jgkLabel})`;
  };

  return (
    <LocalOpsSheet open={open} onClose={onClose} title="Task">
      <div className="localops-badge-inline">
        <JGLocalOpsMark />
      </div>
      <div className="localops-detail-hero">
        <div className="localops-detail-hero__top">
          <div className="localops-chip-group">
            <span className="localops-pill-chip">{label}</span>
            <span className="localops-pill-chip">{statusLabel}</span>
            {communityTask ? (
              <span className="localops-pill-chip localops-pill-chip--verified">
                Community Task
              </span>
            ) : null}
            {task?.sponsored || task?.isSponsored ? (
              <span className="localops-pill-chip">Sponsored</span>
            ) : null}
            {isCreator && acceptedPlayerId ? (
              <span className="localops-pill-chip localops-pill-chip--owner">
                Accepted by {acceptedPlayerId}
              </span>
            ) : null}
          </div>
          {availability ? (
            <div className="localops-presence-chip">
              <span
                className={`localops-presence-dot ${
                  availability === 'busy'
                    ? 'is-busy'
                    : availability === 'idle'
                    ? 'is-idle'
                    : availability === 'offline'
                    ? 'is-offline'
                    : ''
                }`}
              />
              {availability}
            </div>
          ) : null}
        </div>
        <div className="localops-chip-group localops-chip-group--trust">
          {viewerIsVerifiedLocal ? (
            <span className="localops-pill-chip localops-pill-chip--verified">
              Verified Local
            </span>
          ) : null}
          {communityTask ? (
            <span className="localops-pill-chip">No cash payout</span>
          ) : null}
          {isPaymentReady ? (
            <span className="localops-pill-chip localops-pill-chip--ready">
              Payment Ready
            </span>
          ) : isQuoteRequired ? (
            <span className="localops-pill-chip">Quote Required</span>
          ) : null}
          {isFunded ? (
            <span className="localops-pill-chip localops-pill-chip--funded">
              Funded
            </span>
          ) : null}
        </div>
        <div className="localops-owner-row">
          <span className="localops-avatar">
            {ownerPhoto ? (
              <img src={ownerPhoto} alt="" />
            ) : (
              <span className="localops-avatar__fallback">
                {String(ownerLabel || '?').charAt(0).toUpperCase()}
              </span>
            )}
            {availability ? (
              <span
                className={`localops-presence-dot ${
                  availability === 'busy'
                    ? 'is-busy'
                    : availability === 'idle'
                    ? 'is-idle'
                    : availability === 'offline'
                    ? 'is-offline'
                    : ''
                }`}
              />
            ) : null}
          </span>
          <div>
            <div className="localops-owner-title">
              {ownerLabel}
              {isCreator ? ' (You)' : ''}
            </div>
            <div className="localops-helper">Local requester</div>
          </div>
          {isCreator && acceptedPlayerId ? (
            <div className="localops-accepted-by">
              {acceptedPlayerPhoto ? (
                <img
                  src={acceptedPlayerPhoto}
                  alt={acceptedPlayerId}
                  className="localops-accepted-by__avatar"
                />
              ) : (
                <span className="localops-accepted-by__avatar localops-avatar__fallback">
                  {(acceptedPlayerId || '?').slice(0, 1).toUpperCase()}
                </span>
              )}
              <div className="localops-accepted-by__meta">
                <div className="localops-helper">Accepted by</div>
          <div className="localops-owner-title">{acceptedPlayerId}</div>
        </div>
      </div>
    ) : null}

      {isCreator && acceptedPlayerId ? (
        <div className="localops-capsule-panel">
          <div className="localops-capsule-header">
            Task Helper (task-scoped)
          </div>
          {capsuleLoading ? (
            <div className="localops-capsule-text">Loading identity…</div>
          ) : capsuleError ? (
            <div className="localops-capsule-text localops-capsule-text--muted">
              {capsuleError}
            </div>
          ) : capsule ? (
            <>
              <div className="localops-capsule-title">
                {capsule.capsule?.displayName || 'LocalOps contributor'}
              </div>
              <div className="localops-capsule-text">
                {capsule.capsule?.trustSignals?.completedCount || 0} completions •{' '}
                {communityTask
                  ? 'verified contribution'
                  : capsule.capsule?.trustSignals?.payoutVerified
                    ? 'payout verified'
                    : 'payout pending'}
              </div>
              <div className="localops-capsule-hint">
                Task-scoped identity. Verified by LocalOps activity — not public profile.
              </div>
            </>
          ) : null}
        </div>
      ) : null}
        </div>
        <h3 className="localops-detail-title">
          {task?.title || task?.name || task?.meta?.title || 'Task'}
        </h3>
        <div className="localops-detail-sub">{displayLocationLabel}</div>
        <div className="localops-detail-sub">
          {isRouteUnlockTask
            ? 'For safety, exact destination details unlock when the helper confirms they are on the way.'
            : 'For safety, the pickup address is shared only after a helper accepts.'}
        </div>
        {taskPayoutLine ? (
          <div className="localops-price-row" style={{ marginTop: 12, marginBottom: 8 }}>
            <div className="localops-detail-sub" style={{ fontWeight: 700 }}>
              {taskPayoutLine}
            </div>
            {priceJgkLabel ? (
              <div className="localops-helper">
                Funded: {priceJgkLabel} held until completion is verified.
              </div>
            ) : null}
            {netPayoutLine ? (
              <div className="localops-helper">{netPayoutLine}</div>
            ) : null}
          </div>
        ) : null}
      </div>
      {communityTask ? (
        <div className="localops-section">
          <LocalOpsCompletionFlowHint variant="communityTask" />
        </div>
      ) : null}
      {lifecycleProof ? (
        <div className="localops-section" data-testid="localops-lifecycle-proof">
          <div className="localops-live-title">{lifecycleProof.title}</div>
          {lifecycleProof.detail ? (
            <div className="localops-helper">{lifecycleProof.detail}</div>
          ) : null}
          {lifecycleProof.priority ? (
            <div className="localops-helper">{lifecycleProof.priority}</div>
          ) : null}
        </div>
      ) : null}
      {showCompletionProofShare ? (
        <div className="localops-section" data-testid="localops-completion-proof-share">
          <div className="localops-live-title">Share proof</div>
          <div className="localops-helper">
            Show {completionProofRegion} that local tasks are getting done.
          </div>
          <div className="localops-actions" style={{ marginTop: 12 }}>
            <button
              type="button"
              className="localops-btn"
              onClick={handleShareProof}
            >
              Share proof
            </button>
            <button
              type="button"
              className="localops-btn localops-btn--ghost"
              onClick={() => history.push(completionProofSharePath)}
            >
              View public proof
            </button>
          </div>
        </div>
      ) : null}
      {task?.liveTrackingEnabled &&
      (isCreator
        ? ['accepted', 'started', 'in_progress'].includes(status)
        : startedStatuses.has(status)) ? (
        <div className="localops-section">
          <div className="localops-task-live-card" data-testid="localops-task-live-card">
            <div className="localops-task-live-card__header">
              <span className="localops-pill-chip">{label}</span>
              <span className="localops-pill-chip localops-pill-chip--ready">
                {statusLabel}
              </span>
            </div>
            <div className="localops-task-live-card__title">
              Private live route
            </div>
            <div className="localops-task-live-card__copy">
              Route privacy is on. Task route details stay visible only to the
              requester and accepted helper while help is in progress.
            </div>
          </div>
        </div>
      ) : null}
      {acceptedHelperWaitingToStart ? (
        <div className="localops-section" data-testid="localops-route-unlock-copy">
          <div className="localops-live-title">Go to pickup first</div>
          <div className="localops-helper">
            Open the pickup in Maps. When you arrive at pickup, start this
            {isRideRequest ? ' ride' : ' task'} to unlock the
            {isRideRequest ? ' destination' : ' drop-off'}.
          </div>
          {isRideRequest ? (
            <div className="localops-helper">
              {RIDE_HELP_ACCEPTED_HELPER_COPY}
            </div>
          ) : null}
          {preStartPickupMapsHref ? (
            <div className="localops-unlocked-location__actions">
              <a
                className="localops-btn"
                href={preStartPickupMapsHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="localops-prestart-pickup-maps-cta"
              >
                Open pickup in Maps
              </a>
            </div>
          ) : null}
        </div>
      ) : null}
      {showUnlockedLocationPanel ? (
        <div
          className="localops-section localops-unlocked-location"
          data-testid="localops-unlocked-location-panel"
          ref={unlockedLocationPanelRef}
          tabIndex={-1}
        >
          <div className="localops-unlocked-location__eyebrow">
            Address unlocked
          </div>
          <div className="localops-unlocked-location__title">
            Go here next
          </div>
          <div className="localops-unlocked-location__copy">
            Open the address in maps or message the requester if anything is
            unclear.
          </div>
          <div className="localops-unlocked-location__details">
            {pickupAddress ? (
              <div className="localops-unlocked-location__row">
                <span>Pickup</span>
                <strong>{pickupAddress}</strong>
              </div>
            ) : null}
            {deliveryAddress ? (
              <div className="localops-unlocked-location__row">
                <span>Destination</span>
                <strong>{deliveryAddress}</strong>
              </div>
            ) : null}
            {ownerLabel ? (
              <div className="localops-unlocked-location__row">
                <span>Requester</span>
                <strong>{ownerLabel}</strong>
              </div>
            ) : null}
          </div>
          <div className="localops-unlocked-location__actions">
            {openMapsHref ? (
              <a
                className="localops-btn"
                href={openMapsHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="localops-open-maps-cta"
              >
                {openMapsLabel}
              </a>
            ) : null}
            {!isCreator && isAccepted && onMessage ? (
              <button
                type="button"
                className="localops-btn localops-btn--ghost"
                onClick={() => onMessage(task)}
              >
                Message requester
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
      {(showPickupAddress || showDeliveryAddress) ? (
        <div className="localops-section">
          {showPickupAddress ? (
            <div className="localops-helper">
              <strong>
                {showPrivateAddresses || showAcceptedPickupBeforeStart
                  ? 'Pickup address (private):'
                  : 'Pickup address:'}
              </strong>{' '}
              {pickupAddress}
            </div>
          ) : null}
          {showDeliveryAddress ? (
            <div className="localops-helper">
              <strong>Delivery address (private):</strong> {deliveryAddress}
            </div>
          ) : null}
        </div>
      ) : null}
      {taskDescriptionText ? (
        <p className="localops-detail-desc localops-detail-desc--formatted">
          {taskDescriptionText}
        </p>
      ) : null}
      {showPayout ? (
        <div className="localops-section">
          {isCompleted ? (
            <div className="localops-success-card">
              <div className="localops-success-title">
                {'Task completed \uD83C\uDF89'}
              </div>
              <div className="localops-success-subtitle">
                Your JGK Balance has been updated.
              </div>
            </div>
          ) : (
            <h4>Payout after verification</h4>
          )}
          {isCompleted ? (
            <>
              <div className="localops-helper">
                That was real local value in motion.
              </div>
              <div className="localops-helper">
                Completed actions keep communities moving.
              </div>
            </>
          ) : null}
          <div className="localops-pay-card">
            <div className="localops-pay-row">
              <span>Task total</span>
              <span className="localops-pay-amount">
                {formatMoneyPair(
                  payoutSummary.totalUsdLabel,
                  payoutSummary.totalJgkLabel,
                )}
              </span>
            </div>
            <div className="localops-pay-row">
              <span>Platform fee ({payoutSummary.feePercentLabel})</span>
              <span className="localops-pay-amount">
                {formatMoneyPair(
                  payoutSummary.feeUsdLabel,
                  payoutSummary.feeJgkLabel,
                  { negative: true },
                )}
              </span>
            </div>
            {payoutSummary.hasPlatformBoost ? (
              <div className="localops-pay-row">
                <span>Local activity boost</span>
                <span className="localops-pay-amount">
                  {formatMoneyPair(
                    payoutSummary.boostUsdLabel,
                    payoutSummary.boostJgkLabel,
                    { positive: true },
                  )}
                </span>
              </div>
            ) : null}
            <div className="localops-pay-row localops-pay-row--emphasis">
              <span>{isCreator ? 'Helper receives' : 'You receive'}</span>
              <span className="localops-pay-amount">
                {formatMoneyPair(
                  payoutSummary.netUsdLabel,
                  payoutSummary.netJgkLabel,
                )}
              </span>
            </div>
          </div>
          <div className="localops-helper">
            {payoutSummary.hasPlatformBoost
              ? 'Platform support is boosting this task payout.'
              : 'We take a small platform fee to keep LocalOps running.'}
          </div>
          {isCompleted ? (
            <>
              <div className="localops-earn-row">
                <CheckCircleOutlineIcon
                  className="localops-earn-icon"
                  fontSize="small"
                />
                <span>You earned {payoutSummary.netJgkLabel}.</span>
              </div>
              <div className="localops-getpaid">
                <div className="localops-getpaid__title">Get paid</div>
                <div className="localops-helper">
                  You can use your JGK Balance or cash out anytime.
                </div>
                <div className="localops-success-actions">
                  <button
                    type="button"
                    className="localops-btn"
                    onClick={handleCashoutNow}
                  >
                    Cash out now
                  </button>
                  <button
                    type="button"
                    className="localops-btn localops-btn--ghost"
                    onClick={handleViewWallet}
                  >
                    View wallet
                  </button>
                </div>
                <div className="localops-getpaid__meta localops-helper">
                  No minimum balance required. No hidden fees. You're always in
                  control of your balance.
                </div>
              </div>
            </>
          ) : null}
        </div>
      ) : null}
      {task?.etaMinutes && !lifecycleProof ? (
        <div className="localops-helper">ETA: {task.etaMinutes} min</div>
      ) : null}
      {mediaList.length ? (
        <div className="localops-media-hero">
          <PostMediaCarousel
            item={task}
            autoPlay={false}
            enableZoom
            maxHeight="320px"
          />
        </div>
      ) : null}
      {verifiedLocalLocked ? (
        <div className="localops-section">
          <div className="localops-helper" style={{ fontWeight: 700 }}>
            Verified Local required
          </div>
          <div className="localops-helper">
            This task is for Verified Local helpers. Verified Local starts at
            Action Credit 1, earned from verified local completions.
          </div>
          <div className="localops-helper">
            Your Action Credit: {Math.max(0, viewerActionCreditTotal)} / 1
          </div>
          {hasOpenUnlockTask && typeof onFindOpenTask === 'function' ? (
            <div className="localops-actions">
              <button
                type="button"
                className="localops-btn"
                onClick={onFindOpenTask}
              >
                Find open task
              </button>
              <button
                type="button"
                className="localops-btn localops-btn--ghost"
                onClick={onClose}
              >
                Back to map
              </button>
            </div>
          ) : (
            <>
              <div className="localops-helper">
                No open tasks nearby right now.
              </div>
              <div className="localops-helper">
                Check back soon or return to the map.
              </div>
              <div className="localops-actions">
                <button
                  type="button"
                  className="localops-btn"
                  onClick={onClose}
                >
                  Back to map
                </button>
              </div>
            </>
          )}
        </div>
      ) : null}
      {canShowAccept && !verifiedLocalLocked ? (
        <div className="localops-section">
          <div className="localops-helper">
            {communityTask
              ? 'Accept when you are ready to help. The Community Partner verifies completion. No cash payout is attached. Verified completion strengthens your Action Credit record.'
              : `Accept when you're ready to complete the work. The requester verifies completion, then your payout is released to your JGK Balance.${
                  actionCreditOutcomeGuaranteed
                    ? ' Verified completion strengthens your Action Credit record.'
                    : ''
                }`}
          </div>
          {isRideRequest ? (
            <div className="localops-helper">{RIDE_HELP_TASKER_REVIEW_COPY}</div>
          ) : null}
        </div>
      ) : null}
      {canRelease ? (
        <div className="localops-section">
          <div className="localops-helper">
            Can&apos;t do this task? Release it so another local helper can
            accept it.
          </div>
        </div>
      ) : null}
      {canShowRenotifyHelpers ? (
        <div className="localops-section">
          <div className="localops-helper">{renotifyHelpersCopy}</div>
        </div>
      ) : null}
      {canShowRaisePay ? (
        <div className="localops-section">
          <div className="localops-helper">
            Raise funded task pay before a helper accepts.
          </div>
        </div>
      ) : null}
      {canReopen ? (
        <div className="localops-section">
          <div className="localops-helper">
            No confirmation yet. You can message the helper or reopen this task
            so nearby helpers are notified again.
          </div>
        </div>
      ) : null}
      {canComplete ? (
        <div className="localops-section">
          <LocalOpsCompletionFlowHint
            variant={communityTask ? 'communityTask' : 'paidRequester'}
            showProblemCopy={!communityTask}
          />
        </div>
      ) : null}
      <div className="localops-actions">
        {canEditPublicDetails ? (
          <button
            type="button"
            className="localops-btn localops-btn--ghost"
            onClick={() => onEdit?.(task)}
          >
            Edit details
          </button>
        ) : null}
        {canShowAccept && !verifiedLocalLocked ? (
          <button
            type="button"
            className="localops-btn"
            disabled={!canAccept}
            onClick={() => {
              if (!canAccept) return;
              onAccept?.(task);
            }}
          >
            {communityTask ? 'Help with this task' : 'Accept task'}
          </button>
        ) : null}
        {canStart ? (
          <button
            type="button"
            className="localops-btn"
            data-testid={isRouteUnlockTask ? 'localops-route-unlock-start-cta' : undefined}
            onClick={() => {
              if (startLocked) return;
              setStartLocked(true);
              onStart?.(task);
            }}
            disabled={startLocked}
          >
            {isRouteUnlockTask ? routeUnlockStartLabel : 'Start'}
          </button>
        ) : null}
        {canRelease ? (
          <button
            type="button"
            className="localops-btn localops-btn--ghost"
            onClick={() => {
              if (typeof window !== 'undefined') {
                const confirmed = window.confirm(
                  'Release this task and make it available again? No payout will be released.',
                );
                if (!confirmed) return;
              }
              onRelease?.(task);
            }}
          >
            I can&apos;t do this task
          </button>
        ) : null}
        {canMessageHelper ? (
          <button
            type="button"
            className="localops-btn localops-btn--ghost"
            onClick={() =>
              onMessage({
                ...task,
                ownerId: messageHelperTargetId,
              })
            }
          >
            Message helper
          </button>
        ) : null}
        {canShowRenotifyHelpers ? (
          <button
            type="button"
            className="localops-btn localops-btn--ghost"
            onClick={async () => {
              if (renotifyHelpersDisabled) return;
              setRenotifyLocked(true);
              try {
                await onRenotifyHelpers?.(task);
              } finally {
                setRenotifyLocked(false);
              }
            }}
            disabled={renotifyHelpersDisabled}
          >
            Notify helpers again
          </button>
        ) : null}
        {canShowRaisePay ? (
          <button
            type="button"
            className="localops-btn localops-btn--ghost"
            aria-expanded={raisePayOpen}
            aria-controls="localops-raise-pay-panel"
            onClick={toggleRaisePayPanel}
          >
            {raisePayOpen ? 'Close pay raise' : 'Raise pay & notify helpers'}
          </button>
        ) : null}
        {canShowRaisePay && raisePayOpen ? (
          <form
            id="localops-raise-pay-panel"
            className="localops-raise-pay-panel"
            onSubmit={handleRaisePaySubmit}
          >
            <div className="localops-raise-pay-grid">
              <label className="localops-raise-pay-field">
                <span>New task pay</span>
                <input
                  type="number"
                  inputMode="decimal"
                  min={
                    Number.isFinite(minimumRaisePriceUsd)
                      ? minimumRaisePriceUsd
                      : undefined
                  }
                  step="0.01"
                  value={raisePayAmount}
                  onChange={(event) => {
                    setRaisePayAmount(event.target.value);
                    setRaisePayStatus('');
                    setRaisePayMessage('');
                    setRaisePayOperationKey('');
                  }}
                  disabled={raisePayLocked}
                />
              </label>
              <div className="localops-raise-pay-facts">
                <div>
                  <span>Current pay</span>
                  <strong>{formatUsd(priceDisplayUsd)}</strong>
                </div>
                <div>
                  <span>Minimum raise</span>
                  <strong>{formatUsd(minimumRaiseUsd)}</strong>
                </div>
                {Number.isFinite(raisePayDeltaUsd) && raisePayDeltaUsd > 0 ? (
                  <div>
                    <span>Increase</span>
                    <strong>{formatUsd(raisePayDeltaUsd)}</strong>
                  </div>
                ) : null}
              </div>
            </div>
            {raisePayPreview ? (
              <div className="localops-raise-pay-preview">
                <div className="localops-pay-row">
                  <span>New task total</span>
                  <strong className="localops-pay-amount">
                    {raisePayPreview.totalUsdLabel}
                  </strong>
                </div>
                <div className="localops-pay-row">
                  <span>Estimated helper payout</span>
                  <strong className="localops-pay-amount">
                    {raisePayPreview.netUsdLabel}
                  </strong>
                </div>
              </div>
            ) : null}
            {raisePayMessage || raisePayValidationMessage ? (
              <div
                className={`localops-raise-pay-status ${
                  raisePayStatus === 'success'
                    ? 'localops-raise-pay-status--success'
                    : raisePayStatus === 'pending_funding' ||
                      raisePayStatus === 'pending'
                    ? 'localops-raise-pay-status--pending'
                    : 'localops-raise-pay-status--error'
                }`}
                role={
                  raisePayStatus === 'success' ||
                  raisePayStatus === 'pending_funding' ||
                  raisePayStatus === 'pending'
                    ? 'status'
                    : 'alert'
                }
              >
                {raisePayMessage || raisePayValidationMessage}
              </div>
            ) : null}
            <div className="localops-raise-pay-actions">
              <button
                type="submit"
                className="localops-btn"
                disabled={!raisePayReady || raisePayLocked}
              >
                {raisePayLocked ? 'Raising pay...' : 'Raise pay'}
              </button>
            </div>
          </form>
        ) : null}
        {canReopen ? (
          <button
            type="button"
            className="localops-btn localops-btn--ghost"
            onClick={() => {
              if (typeof window !== 'undefined') {
                const confirmed = window.confirm(
                  'Reopen this task and notify nearby helpers again?',
                );
                if (!confirmed) return;
              }
              onReopen?.(task);
            }}
          >
            Reopen task
          </button>
        ) : null}
        {canComplete ? (
          <button
            type="button"
            className="localops-btn"
            onClick={() => onComplete?.(task)}
          >
            Verify completion
          </button>
        ) : null}
        {canShareLive && !['completed', 'cancelled', 'expired'].includes(status) ? (
          <button
            type="button"
            className="localops-btn localops-btn--ghost"
            onClick={() => onShare?.(task)}
          >
            Share live route
          </button>
        ) : null}
        {shouldShowAv ? (
          <button
            type="button"
            className="localops-btn localops-btn--ghost"
            onClick={handleAvRequest}
          >
            Start A/V
          </button>
        ) : null}
        {shouldShare ? (
          <button
            type="button"
            className="localops-btn localops-btn--cta"
            onClick={() => onShare?.(task)}
          >
            {communityTask ? 'Share Community Task' : 'Share & Earn JGK'}
          </button>
        ) : null}
        {!isCreator && isAccepted && onMessage ? (
          <button
            type="button"
            className="localops-btn localops-btn--ghost"
            onClick={() => onMessage(task)}
          >
            Message owner
          </button>
        ) : null}
        {canCancel ? (
          <button
            type="button"
            className="localops-btn localops-btn--danger"
            onClick={() => {
              if (typeof window !== 'undefined') {
                const confirmed = window.confirm(
                  'Remove this task from the map?',
                );
                if (!confirmed) return;
              }
              onCancel?.(task);
            }}
          >
            Remove task
          </button>
        ) : null}
        {showShareHelper ? (
          <div className="localops-earn-row">
            <CheckCircleOutlineIcon
              className="localops-earn-icon"
              fontSize="small"
            />
            <span>
              {communityTask
                ? 'Invite locals to help with this Community Task.'
                : 'Nice! Earn JGK Balance for sharing LocalOps.'}
            </span>
          </div>
        ) : null}
      </div>
    </LocalOpsSheet>
  );
}
